import { KeyRoundIcon, TextField } from "@strust/ui";
import { useIntl } from "react-intl";
import { useFormField, type FormField } from "./form-field.ts";
import { twoFactorMessages } from "./two-factor-messages.ts";

/** A 6-digit TOTP code (RFC 6238). */
export const isTotpCode = (value: string): boolean => /^\d{6}$/.test(value);

const digitsOnly = (raw: string): string => raw.replace(/\D/g, "").slice(0, 6);

/**
 * The state of an authenticator code field: digits only, and the "6 digits" message once the user
 * left the field or submitted. `required: false` accepts any value (the field is not shown).
 */
export function useTotpCodeField({ required = true }: { readonly required?: boolean } = {}) {
  const intl = useIntl();
  return useFormField({
    sanitize: digitsOnly,
    validate: (value) =>
      !required || isTotpCode(value) ? undefined : intl.formatMessage(twoFactorMessages.codeInvalid),
  });
}

export interface TotpCodeFieldProps {
  /** From `useTotpCodeField`. */
  readonly field: FormField;
  readonly label?: string;
  readonly autoFocus?: boolean;
  readonly className?: string;
}

/** The code of the authenticator app: digits only, one-time-code autofill, never kept after use. */
export function TotpCodeField({ field, label, autoFocus = false, className }: TotpCodeFieldProps) {
  const intl = useIntl();
  return (
    <TextField
      label={label ?? intl.formatMessage(twoFactorMessages.code)}
      hint={intl.formatMessage(twoFactorMessages.codeHint)}
      name="totp-code"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      mono
      autoFocus={autoFocus}
      leadingIcon={<KeyRoundIcon />}
      {...field.inputProps}
      {...(className === undefined ? {} : { className })}
    />
  );
}
